// src/hooks/usePractitionerAvailability.ts
import { useMemo } from "react";
import { QueryKey, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";

import { createScheduleOverride, fetchPractitionerAvailability, updateScheduleOverride } from "../../api/admin/scheduleOverrideApi";
import {
	createClinicClosure,
	createUnavailableBlock,
	deleteClinicClosure,
	deleteUnavailableBlock,
	updateClinicClosure,
	updateUnavailableBlock,
} from "../../api/admin/unavailableBlockApi";
import { useNotifications } from "../../context/NotificationContext";
import { PractitionerAvailability, ScheduleOverride, UnavailableBlock, UnavailableBlockPayload, createEmptyTimeBlock } from "../../types/admin/TeamTypes";
import { WallClockWindow } from "../../utils/appointments/availability";
import type { UserUuid } from "../../types/identity";

export const AVAILABILITY_QUERY_KEY = "practitioner-availability";

const EMPTY: PractitionerAvailability = { overrides: {}, blocks: {}, clinic_closures: [] };

export interface UsePractitionerAvailabilityResult {
	overrides: Record<UserUuid, ScheduleOverride[]>;
	blocks: Record<UserUuid, UnavailableBlock[]>;
	clinicClosures: UnavailableBlock[];
	isLoading: boolean;
	isError: boolean;
	error: Error | null;
}

// from/to are "YYYY-MM-DD" strings, never Dates — a Date re-keys the query on every render.
export const usePractitionerAvailability = (practitionerIds: UserUuid[], from: string, to: string): UsePractitionerAvailabilityResult => {
	// Sorted in the key: the same roster in a different order is the same request.
	const sortedIds = useMemo(() => [...practitionerIds].sort(), [practitionerIds]);
	const enabled = sortedIds.length > 0 && Boolean(from) && Boolean(to);

	const query = useQuery<PractitionerAvailability, Error>({
		queryKey: [AVAILABILITY_QUERY_KEY, sortedIds, from, to],
		queryFn: () => fetchPractitionerAvailability(sortedIds, from, to),
		enabled,
	});

	const data = query.data ?? EMPTY;

	return {
		overrides: data.overrides,
		blocks: data.blocks,
		clinicClosures: data.clinic_closures,
		isLoading: query.isLoading,
		isError: query.isError,
		error: query.error ?? null,
	};
};

export interface CreateBlocksVariables {
	// null = clinic closure; the caller never mixes null with ids.
	targets: Array<UserUuid | null>;
	payload: UnavailableBlockPayload;
	// For the failure toast — names, never ids.
	describe: (target: UserUuid | null) => string;
}

export interface CreateBlocksResult {
	failed: Array<UserUuid | null>;
	firstError?: unknown;
}

export interface UpdateBlockVariables {
	userId: UserUuid | null;
	payload: UnavailableBlockPayload;
	blockId: number;
}

export interface DeleteBlockVariables {
	userId: UserUuid | null;
	blockId: number;
}

type Snapshot = [QueryKey, PractitionerAvailability | undefined];

const errorMessage = (error: unknown): string | undefined => {
	if (axios.isAxiosError(error)) return error.response?.data?.message ?? error.message;
	return error instanceof Error ? error.message : undefined;
};

export const SCHEDULE_PERMISSION_DENIED = "You do not have permission to change this practitioner's schedule.";

// The affordance gate is cosmetic, so a 403 is reachable and must say why rather than read as a generic failure.
const writeFailure = (error: unknown): string | undefined =>
	axios.isAxiosError(error) && error.response?.status === 403 ? SCHEDULE_PERMISSION_DENIED : errorMessage(error);

// Every cached availability page rewritten the same way, with the snapshots needed to put them back.
const useOptimisticBlocks = () => {
	const queryClient = useQueryClient();

	const apply = async (userId: UserUuid | null, revise: (rows: UnavailableBlock[]) => UnavailableBlock[]): Promise<{ snapshots: Snapshot[] }> => {
		await queryClient.cancelQueries({ queryKey: [AVAILABILITY_QUERY_KEY] });
		const snapshots = queryClient.getQueriesData<PractitionerAvailability>({ queryKey: [AVAILABILITY_QUERY_KEY] }) as Snapshot[];

		snapshots.forEach(([key, previous]) => {
			if (!previous) return;

			queryClient.setQueryData<PractitionerAvailability>(
				key,
				userId === null
					? { ...previous, clinic_closures: revise(previous.clinic_closures) }
					: { ...previous, blocks: { ...previous.blocks, [userId]: revise(previous.blocks[userId] ?? []) } }
			);
		});

		return { snapshots };
	};

	const rollback = (context?: { snapshots: Snapshot[] }) =>
		context?.snapshots.forEach(([key, previous]) => queryClient.setQueryData<PractitionerAvailability | undefined>(key, previous));

	const settle = () => queryClient.invalidateQueries({ queryKey: [AVAILABILITY_QUERY_KEY] });

	return { apply, rollback, settle };
};

// The dialog never sends this; it exists so a caller mistake makes no request rather than a corrupted cache.
const isMixedTargets = (targets: Array<UserUuid | null>): boolean => targets.includes(null) && targets.some((target) => target !== null);

export const useCreateUnavailableBlocks = () => {
	const { showError } = useNotifications();
	const cache = useOptimisticBlocks();

	return useMutation<CreateBlocksResult, unknown, CreateBlocksVariables, { snapshots: Snapshot[] }>({
		// Resolves with a failed list rather than rejecting, so one 403 among N never rolls back the others.
		mutationFn: async ({ targets, payload }) => {
			if (isMixedTargets(targets)) return { failed: [] };

			const settled = await Promise.allSettled(targets.map((target) => (target === null ? createClinicClosure(payload) : createUnavailableBlock(target, payload))));

			const failed: Array<UserUuid | null> = [];
			let firstError: unknown;
			settled.forEach((result, index) => {
				if (result.status === "rejected") {
					failed.push(targets[index]);
					if (firstError === undefined) firstError = result.reason;
				}
			});

			return { failed, firstError };
		},
		// The drag gesture must paint before the round trip; rolled back below only if onMutate itself throws.
		onMutate: async ({ targets, payload }) => {
			if (isMixedTargets(targets)) return { snapshots: [] };

			// Each apply() snapshots the cache as it currently stands, so only the FIRST call's snapshot is the true pre-mutation state to roll back to.
			let snapshots: Snapshot[] = [];
			for (const [index, target] of targets.entries()) {
				const now = new Date().toISOString();
				// Negative id marks the row as optimistic; offset by index so same-tick targets don't collide.
				const optimistic: UnavailableBlock = {
					id: -Date.now() - index,
					user_id: target,
					starts_at: payload.starts_at,
					ends_at: payload.ends_at,
					reason: payload.reason ?? null,
					block_type: payload.block_type,
					created_by: "",
					created_at: now,
					updated_at: now,
				};

				const applied = await cache.apply(target, (rows) => [...rows, optimistic]);
				if (index === 0) snapshots = applied.snapshots;
			}

			return { snapshots };
		},
		onError: (error, _variables, context) => {
			cache.rollback(context);
			showError("Could not block out the selected time", writeFailure(error));
		},
		onSuccess: ({ failed, firstError }, { describe, targets }, context) => {
			// Every target failed: nothing was created, so don't leave the optimistic rows painted until (or if) the refetch lands.
			if (targets.length > 0 && failed.length === targets.length) cache.rollback(context);
			cache.settle();
			if (failed.length > 0) {
				showError(`Could not block out ${failed.map(describe).join(", ")}`, writeFailure(firstError));
			}
		},
	});
};

export const useUpdateUnavailableBlock = () => {
	const { showError } = useNotifications();
	const cache = useOptimisticBlocks();

	return useMutation<unknown, unknown, UpdateBlockVariables, { snapshots: Snapshot[] }>({
		mutationFn: ({ userId, blockId, payload }) => (userId === null ? updateClinicClosure(blockId, payload) : updateUnavailableBlock(userId, blockId, payload)),
		// A dragged block must stay where it was dropped rather than snapping back for the round trip.
		onMutate: ({ userId, blockId, payload }) => cache.apply(userId, (rows) => rows.map((row) => (row.id === blockId ? { ...row, ...payload } : row))),
		onError: (error, _variables, context) => {
			cache.rollback(context);
			showError("Could not update the unavailable block", writeFailure(error));
		},
		onSuccess: () => {
			cache.settle();
		},
	});
};

export const useDeleteUnavailableBlock = () => {
	const { showError } = useNotifications();
	const cache = useOptimisticBlocks();

	return useMutation<unknown, unknown, DeleteBlockVariables, { snapshots: Snapshot[] }>({
		mutationFn: ({ userId, blockId }) => (userId === null ? deleteClinicClosure(blockId) : deleteUnavailableBlock(userId, blockId)),
		onMutate: ({ userId, blockId }) => cache.apply(userId, (rows) => rows.filter((row) => row.id !== blockId)),
		onError: (error, _variables, context) => {
			cache.rollback(context);
			showError("Could not delete the unavailable block", writeFailure(error));
		},
		onSuccess: () => {
			cache.settle();
		},
	});
};

export interface AddAvailabilityVariables {
	userId: UserUuid;
	// null creates the single-date override; otherwise the existing custom row is replaced.
	overrideId: number | null;
	dateKey: string;
	windows: WallClockWindow[];
	reason?: string | null;
}

// The OTHER write mechanism, deliberately not folded into the block mutations above: availability can
// only be ADDED by reshaping the day, so it goes to schedule-overrides and carries HH:mm wall clock.
export const useAddAvailabilityWindow = () => {
	const queryClient = useQueryClient();
	const { showError } = useNotifications();

	return useMutation<unknown, unknown, AddAvailabilityVariables>({
		mutationFn: ({ userId, overrideId, dateKey, windows, reason }) => {
			const payload: Partial<ScheduleOverride> = {
				start_date: dateKey,
				end_date: dateKey,
				override_type: "custom",
				schedule_data: windows.map((window) => ({ ...createEmptyTimeBlock(), start: window.start, end: window.end })),
				...(reason ? { reason } : {}),
			};

			return overrideId === null ? createScheduleOverride(userId, payload) : updateScheduleOverride(userId, overrideId, payload);
		},
		onError: (error) => showError("Could not add availability", writeFailure(error)),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [AVAILABILITY_QUERY_KEY] });
		},
	});
};
